import type { ClientBuilderUserPermission } from "api-types/builderUsers/builderUsersResponses";
import { HOME_PLAN_CATEGORY_LABELS } from "api-types/homePlans/homePlanFields";
import type { ClientHomePlan, ClientHomePlanHydratedNoDoc } from "api-types/homePlans/homePlanResponses";
import type { HomePlanCategory } from "api-types/homePlans/homePlanTypes";
import type { ClientUserHydrated } from "api-types/users/usersResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import { assertIsNotNullOrUndefined, ensureIsString } from "common/utils/assertOfType";
import { assertValidatesSchemaTODO } from "doc/schema/docSchemaAssertion";
import useHomePlanEvents from "lib/components/builders/communities/utils/useHomePlanEvents";
import IconHomePlanCategory from "lib/components/typedIcons/IconHomePlanCategory";
import { DELETE_HOME_PLANS_MESSAGE, DELETE_HOME_PLAN_MESSAGE } from "lib/constants/messages";
import { useFetch } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import getUserFullName from "lib/utils/userName";
import type { FormEvent } from "react";
import { useState } from "react";
import ConfirmDeleteModal from "ui/components/ConfirmDeleteModal";
import Link from "ui/components/Link";
import ExpandableSelect from "ui/forms/ExpandableSelect";
import useAppContext from "ui/hooks/useAppContext";
import IconTrash from "ui/icons/IconTrash";
import styles from "./HomePlanMultiSelect.module.scss";
import { useDeleteHomePlans, useUpdateHomePlans } from "./useHomePlanAPI";

interface HomePlanMultiSelectProps {
  homePlans: ClientHomePlanHydratedNoDoc[];
  onMultiUpdate: (homePlans: ClientHomePlanHydratedNoDoc[]) => void;
  onDelete: (id: string) => void;
}

export default function HomePlanMultiSelect({ homePlans, onMultiUpdate, onDelete }: HomePlanMultiSelectProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { setUserNotification } = useAppContext();
  const { builderId } = useAppRouterParams();
  const sendHomePlanEvent = useHomePlanEvents();
  const { user, isSuperAdmin, hasPermission } = useUserContext();
  const deleteHomePlans = useDeleteHomePlans(builderId ?? "");
  const updateHomePlans = useUpdateHomePlans(builderId ?? "");

  const salesStaffPermission: ClientBuilderUserPermission = "ALLOWED_AS_SALES_STAFF";
  const { data: salesStaff } = useFetch<ClientUserHydrated[]>(
    `${WEB_API_BASE_PATH}/builders/${builderId}/users-internal?permission=${salesStaffPermission}`,
    [builderId],
  );
  const staffOpts = salesStaff?.length
    ? salesStaff.map((sa) => ({
        label: getUserFullName(sa),
        value: sa.id,
      }))
    : [];

  if (isSuperAdmin || user?.tempUserRole === "sales_agent") {
    assertIsNotNullOrUndefined(user);
    staffOpts.push({
      label: getUserFullName(user),
      value: user.id,
    });
  }

  const isMany = homePlans.length > 1;

  // TODO: update these 2 functions to use updateMany and deleteMany endpoints when available
  const handleDelete = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsDeleting(true);
    assertIsNotNullOrUndefined(builderId);
    const result = await deleteHomePlans.mutateAsync(homePlans.map((homePlan) => homePlan.id));
    const successfulHomePlanIds = new Set(result.successfulHomePlanIds);
    homePlans.forEach((homePlan) => {
      if (!successfulHomePlanIds.has(homePlan.id)) {
        return;
      }
      sendHomePlanEvent("config:deleted_homeplan", {
        home_plan_id: homePlan.id,
        home_plan_name: homePlan.planName,
        home_plan_status: homePlan.planStatus,
        home_plan_category: homePlan.category,
        home_plan_sales_agent_email: homePlan.salesStaff?.email,
        home_plan_community_plan_id: homePlan.communityPlanId,
        master_plan: homePlan.masterPlanName,
        lot_id: homePlan.lotId,
        lot_number: homePlan.lot?.lotNumber,
        location_name: homePlan.communityName,
      });
      onDelete(homePlan.id);
    });
    const errors = result.failures.length;
    if (errors) {
      setUserNotification({
        message: `Error deleting ${errors} Home Plan${errors > 1 ? "s" : ""}.`,
        messageType: "error",
      });
    } else {
      setUserNotification({
        message: `${homePlans.length} home plan${isMany ? "s have" : " has"} been deleted.`,
        messageType: "success",
      });
    }

    setIsDeleting(false);
  };

  const updateHomePlan = async (updatedHomePlan: Partial<ClientHomePlan>): Promise<void> => {
    assertIsNotNullOrUndefined(builderId);
    const result = await updateHomePlans.mutateAsync(
      homePlans.map((homePlan) => ({
        homePlanId: homePlan.id,
        fields: {
          ...updatedHomePlan,
          planName: updatedHomePlan.planName ?? undefined,
          optionChoices: updatedHomePlan.optionChoices ?? undefined,
          lockedAt: updatedHomePlan.lockedAt ?? undefined,
          detachedAt: updatedHomePlan.detachedAt ?? undefined,
          notes: updatedHomePlan.notes ?? undefined,
          category: updatedHomePlan.category ?? undefined,
          customerInfo: updatedHomePlan.customerInfo ?? undefined,
        },
      })),
    );

    const successfulHomePlanIds = new Set(result.successfulHomePlanIds);
    onMultiUpdate(
      homePlans
        .filter((homePlan) => successfulHomePlanIds.has(homePlan.id))
        .map((homePlan) => ({ ...homePlan, ...updatedHomePlan })),
    );

    const errors = result.failures.length;
    if (errors) {
      setUserNotification({
        message: `Error updating ${errors} Home Plan${errors > 1 ? "s" : ""}.`,
        messageType: "error",
      });
    } else {
      setUserNotification({
        message: `${homePlans.length} home plan${isMany ? "s have" : " has"} been updated.`,
        messageType: "success",
      });
    }
  };

  return (
    <div className={styles.wrapper}>
      {hasPermission("SET_CATEGORY_ON_HOME_PLANS") && (
        <div className={styles.field}>
          <label>Category:</label>
          <ExpandableSelect
            selectSize="medium"
            placeholder="Update Category"
            options={Object.entries(HOME_PLAN_CATEGORY_LABELS).map(([value, label]) => ({
              label,
              value,
              icon: <IconHomePlanCategory category={value as HomePlanCategory} />,
            }))}
            value={null}
            onChange={(category) => {
              assertValidatesSchemaTODO<HomePlanCategory>(category);
              void updateHomePlan({ category });
            }}
          />
        </div>
      )}
      {staffOpts.length ? (
        <div className={styles.field}>
          <label>Sales Agent:</label>
          <ExpandableSelect
            selectSize="medium"
            placeholder="Update Sales Agent"
            options={[{ label: "None", value: "none" }, ...staffOpts]}
            value={null}
            onChange={(salesStaffId) =>
              updateHomePlan({ salesStaffId: salesStaffId === "none" ? null : ensureIsString(salesStaffId) })
            }
          />
        </div>
      ) : null}
      {hasPermission("DELETE_HOME_PLANS") && (
        <Link onClick={() => setConfirmDelete(true)}>
          <IconTrash size={14} />
          Delete Home Plan{isMany ? "s" : ""}
        </Link>
      )}
      {confirmDelete && (
        <ConfirmDeleteModal
          onClose={() => setConfirmDelete(false)}
          onConfirm={handleDelete}
          entityType={isMany ? `${homePlans.length} Home Plans` : "Home Plan"}
          entityName="Delete All"
          isMany={isMany}
          isDeleting={isDeleting}
          message={isMany ? DELETE_HOME_PLANS_MESSAGE : DELETE_HOME_PLAN_MESSAGE}
        />
      )}
    </div>
  );
}
